import { ImageCropModal } from '@/components/cropper/ImageCropModal';
import Input from '@/components/forms/Input';
import Select from '@/components/forms/Select';
import Textarea from '@/components/forms/Textarea';
import { Upload } from '@/components/upload';
import { useAttributes } from '@/hooks/useAttributes';
import { useDynamicLocale } from '@/hoslog-intl';
import { FileObject } from '@/lib/uploadToAWS';
import { HostManagement } from '@/services/hostManagement/hostManagement.types';
import { ReactElement, useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { HostFormValues } from './premium-stores/form/ManageHostManagement';

type FieldType = 'input' | 'textarea' | 'select' | 'dob' | 'mbti' | 'selectRank';

type FieldConfig = {
  name: keyof PersonalInfoFormValues;
  label: string;
  type: FieldType;
  options?: { label: string; value: string }[];
  placeholder?: string;
  maxLength?: number;
  required?: boolean;
  defaultValue?: string | boolean;
  subLabel?: string;
  subLabelClassName?: string;
  link?: string;
};

type PersonalInfoFormValues = {
  position: string;
  hostName: string;
  dobYear: string;
  dobMonth: string;
  dobDay: string;
  furigana: string;
  rank: string;
  mbti: string;
  title: string;
  birthPlace: string;
  bloodType: string;
  constellation: string;
  height: string;
  profileImage?: FileObject | File | string | null;
};

const personalInfoConfig: FieldConfig[] = [
  {
    name: 'position',
    label: 'storeManagement.hostManagement.form.basicInfo.position',
    type: 'input',
    placeholder: 'storeManagement.hostManagement.form.basicInfo.postPlaceholder',
  },
  {
    name: 'rank',
    label: 'storeManagement.hostManagement.form.basicInfo.rankTitle',
    type: 'selectRank',
    placeholder: 'storeManagement.hostManagement.form.placeholder.select',
  },
  {
    name: 'hostName',
    label: 'storeManagement.hostManagement.form.basicInfo.name',
    type: 'input',
    placeholder: 'storeManagement.hostManagement.form.placeholder.hostName',
    required: true,
  },
  {
    name: 'furigana',
    label: 'storeManagement.hostManagement.form.basicInfo.furigana',
    type: 'input',
    placeholder: 'storeManagement.hostManagement.form.placeholder.furigana',
    required: true,
  },
  {
    name: 'dobYear',
    label: 'storeManagement.hostManagement.form.basicInfo.birthDate',
    type: 'dob',
    placeholder: 'storeManagement.hostManagement.form.placeholder.dob',
  },
  {
    name: 'height',
    label: 'storeManagement.hostManagement.form.basicInfo.height',
    type: 'input',
    placeholder: 'storeManagement.hostManagement.form.basicInfo.heightPlaceholder',
  },
  {
    name: 'constellation',
    label: 'storeManagement.hostManagement.form.basicInfo.constellation',
    type: 'select',
    placeholder: 'storeManagement.hostManagement.form.placeholder.select',
  },
  {
    name: 'birthPlace',
    label: 'storeManagement.hostManagement.form.basicInfo.birthPlace',
    type: 'select',
    placeholder: 'storeManagement.hostManagement.form.placeholder.select',
  },
  {
    name: 'bloodType',
    label: 'storeManagement.hostManagement.form.basicInfo.bloodType',
    type: 'select',
    placeholder: 'storeManagement.hostManagement.form.placeholder.select',
  },
  {
    name: 'mbti',
    label: 'storeManagement.hostManagement.form.basicInfo.mbti',
    type: 'input',
    placeholder: 'storeManagement.hostManagement.form.basicInfo.mbti',
    subLabel: '無料性格診断',
    subLabelClassName: 'text-verification-link underline text-control',
    link: 'https://www.16personalities.com/ja',
    maxLength: 4,
  },
  {
    name: 'title',
    label: 'storeManagement.hostManagement.form.basicInfo.title',
    type: 'input',
    placeholder: 'storeManagement.hostManagement.form.basicInfo.title',
  },
];

type ProfileImageType = {
  alt: string;
  description: string;
  path: string | null;
};

type PersonalInfoProps = {
  hostDetails?: HostFormValues;
  hideField?: string;
  rankOptions?: { label: string; value: string }[];
};

export const PersonalInfo = ({ hostDetails, hideField, rankOptions }: PersonalInfoProps): ReactElement => {
  const { register, control, setValue } = useFormContext<PersonalInfoFormValues>();
  const { t } = useDynamicLocale();

  const { data: zodiac = [] } = useAttributes('zodiac_sign') || {};
  const { data: rank = [] } = useAttributes(hideField === 'rank' || rankOptions ? null : 'rank') || {};
  const { data: bloodTypeOption = [] } = useAttributes('blood_type') || {};
  const { data: birthPlace = [] } = useAttributes('prefectures') || {};
  const { constellation: zodiacVal, bloodType } = hostDetails ?? ({} as HostManagement);

  const selectedMap: Partial<Record<keyof PersonalInfoFormValues, string>> = {
    constellation: zodiacVal,
    bloodType,
    rank: hostDetails?.rank ?? rankOptions?.[0]?.value,
  };

  const [profileImage, setProfileImage] = useState<ProfileImageType>({
    alt: '',
    description: '',
    path: null,
  });
  const [cropSource, setCropSource] = useState<string | null>(null);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);

  const file = useWatch({
    control,
    name: 'profileImage',
  });

  useEffect(() => {
    if (!file) {
      setProfileImage({
        alt: '',
        description: '',
        path: null,
      });
      return;
    }

    if (typeof file === 'string') {
      setProfileImage({
        alt: 'profile image',
        description: '',
        path: file,
      });
      return;
    }

    if (file instanceof File) {
      const objectURL = URL.createObjectURL(file);

      setProfileImage({
        alt: file.name,
        description: '',
        path: objectURL,
      });

      return () => URL.revokeObjectURL(objectURL);
    }
  }, [file]);

  const renderDobRow = (field: FieldConfig) => {
    const label = field.label;

    const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/\D/g, '');
      if (value.length <= 4) {
        setValue('dobYear', value, { shouldValidate: true });
      }
    };

    const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/\D/g, '');
      const numValue = parseInt(value);

      if (value === '') {
        setValue('dobMonth', '');
      } else if (numValue >= 1 && numValue <= 12) {
        setValue('dobMonth', value.slice(0, 2));
      } else if (value.length === 1 && numValue >= 0 && numValue <= 1) {
        setValue('dobMonth', value);
      }
    };

    const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/\D/g, '');
      const numValue = parseInt(value);

      if (value === '') {
        setValue('dobDay', '');
      } else if (numValue >= 1 && numValue <= 31) {
        setValue('dobDay', value.slice(0, 2));
      } else if (value.length === 1 && numValue >= 0 && numValue <= 3) {
        setValue('dobDay', value);
      }
    };

    return (
      <div key="dob-group" className="flex flex-col">
        <label className="text-foreground leading-4 text-sm font-bold">{t(label)}</label>
        <div className="grid grid-cols-3 gap-3">
          <Input placeholder="YYYY" {...register('dobYear')} onChange={handleYearChange} maxLength={4} />
          <Input placeholder="MM" {...register('dobMonth')} onChange={handleMonthChange} maxLength={2} />
          <Input placeholder="DD" {...register('dobDay')} onChange={handleDayChange} maxLength={2} />
        </div>
      </div>
    );
  };
  const personalInfoFields = (field: FieldConfig): ReactElement => {
    if (field.type === 'dob') return renderDobRow(field);

    const props = {
      key: field.name,
      label: field.label,
      subLabel: field.subLabel,
      subLabelClassName: field.subLabelClassName,
      link: field.link,
      placeholder: t(field.placeholder as string),
      maxLength: field.maxLength ?? 50,
      forceRequired: field.required,
      ...register(field.name),
    };
    const apiOptionsMap: Record<string, { label: string; value: string }[]> = {
      constellation: zodiac,
      rank: rankOptions ?? rank,
      birthPlace,
    };

    const finalOptions = apiOptionsMap[field.name] ?? [];
    const checkOption = field.name === 'bloodType' ? bloodTypeOption : finalOptions;
    switch (field.type) {
      case 'textarea':
        return <Textarea {...props} className="resize-none" placeholder={t(field.placeholder as string)} />;
      case 'select':
        return (
          <Select
            {...props}
            options={checkOption ?? []}
            placeholder={t(field.placeholder as string)}
            selected={selectedMap?.[field.name]}
          />
        );
      case 'selectRank':
        return <Select {...props} options={checkOption ?? []} selected={selectedMap?.[field.name]} hideDefaultOption />;
      case 'input':
        return <Input {...props} />;
      default:
        return <></>;
    }
  };

  return (
    <div className="flex gap-5 sm:gap-10 md:gap-15 lg:gap-20">
      <div className="w-[200px] xl:w-[259px] shrink-0">
        <div className="pl-12 ">
          <Controller
            name="profileImage"
            rules={{ required: 'Please upload a profile picture' }}
            control={control}
            render={({ field, fieldState }) => (
              <Upload
                key={uploadKey}
                variant="circle"
                accept=".jpg,.jpeg,.png,.svg,.heic,.heif"
                multiple={false}
                maxSizeMB={5}
                error={fieldState.error?.message}
                defaultPreviewUrl={profileImage.path ?? ''}
                previewClassName="h-[164px] w-[164px]"
                onFilesChange={(files) => {
                  const selected = files?.[0];

                  if (!selected || typeof selected === 'string') {
                    field.onChange('');
                    return;
                  }

                  const rawFile = selected.file as File;
                  const localPreview = selected.preview || URL.createObjectURL(rawFile);
                  setCropSource(localPreview);
                  setIsCropOpen(true);
                }}
                onDefaultPreviewRemove={() => {
                  setProfileImage({
                    alt: '',
                    description: '',
                    path: '',
                  });
                  setValue('profileImage', '', {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
              />
            )}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4.5 w-full">
        {personalInfoConfig.map((field) => {
          const singleCol =
            ['dobYear'].includes(field.name as string) || (hideField === 'rank' && field.name === 'dobYear');
          if (hideField === (field.name as string)) return null;

          return (
            <>
              <div key={field.name} className={'col-span-1'}>
                {personalInfoFields(field)}
              </div>
              {singleCol && <div></div>}
            </>
          );
        })}
      </div>
      <ImageCropModal
        open={isCropOpen}
        imageSrc={cropSource}
        cropShape="round"
        onClose={() => {
          setIsCropOpen(false);

          if (cropSource?.startsWith('blob:')) {
            URL.revokeObjectURL(cropSource);
          }

          setCropSource(null);
          setUploadKey((k) => k + 1);
        }}
        onCropDone={(croppedFile, previewUrl) => {
          const croppedFileObject: FileObject = {
            file: croppedFile,
            id: `${Date.now()}`,
            preview: previewUrl,
          };

          if (cropSource?.startsWith('blob:')) {
            URL.revokeObjectURL(cropSource);
          }

          setProfileImage({
            alt: '',
            description: '',
            path: previewUrl,
          });

          setValue('profileImage', croppedFileObject, {
            shouldValidate: true,
            shouldDirty: true,
          });

          setCropSource(null);
          setIsCropOpen(false);
          setUploadKey((k) => k + 1);
        }}
      />
    </div>
  );
};
